"use client";

import { useEffect, useState } from "react";
import { ErrorNotice, TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { DayLengthResults } from "@/components/tools/astronomy/day-length-results";
import { PlaceFields } from "@/components/tools/astronomy/place-fields";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  type PlaceFieldValues,
  readPlace,
} from "@/lib/tool-kit/astronomy/place-input";
import { nowInZone, parseIsoDay } from "@/lib/tool-kit/astronomy/zone";

/** English strings. A translated content file overrides these through `ui`. */
export const DAY_LENGTH_CALCULATOR_STRINGS = {
  place: "Place",
  custom: "Custom coordinates",
  placeNames: {
    "new-york": "New York, USA",
    chicago: "Chicago, USA",
    denver: "Denver, USA",
    "los-angeles": "Los Angeles, USA",
    anchorage: "Anchorage, USA",
    honolulu: "Honolulu, USA",
    "mexico-city": "Mexico City, Mexico",
    quito: "Quito, Ecuador",
    "sao-paulo": "São Paulo, Brazil",
    "buenos-aires": "Buenos Aires, Argentina",
    reykjavik: "Reykjavik, Iceland",
    london: "London, UK",
    paris: "Paris, France",
    berlin: "Berlin, Germany",
    tromso: "Tromsø, Norway",
    moscow: "Moscow, Russia",
    cairo: "Cairo, Egypt",
    lagos: "Lagos, Nigeria",
    nairobi: "Nairobi, Kenya",
    johannesburg: "Johannesburg, South Africa",
    dubai: "Dubai, UAE",
    mumbai: "Mumbai, India",
    singapore: "Singapore",
    beijing: "Beijing, China",
    tokyo: "Tokyo, Japan",
    sydney: "Sydney, Australia",
    auckland: "Auckland, New Zealand",
  },
  latitude: "Latitude (° north, − for south)",
  longitude: "Longitude (° east, − for west)",
  latitudeHint: "Decimal degrees, for example 51.5 or −33.87.",
  zone: "Time zone",
  locate: "Use my location",
  locating: "Locating…",
  locateFailed:
    "Your browser could not share a location. Pick a city or type coordinates instead.",
  date: "Date",
  today: "Today",
  invalidPlace:
    "Enter a latitude between −90 and 90 and a longitude between −180 and 180.",
  invalidDate: "Enter a valid date.",
  results: "Daylight on {date}",
  daylight: "Length of day",
  duration: "{hours} h {minutes} min",
  decimalHours: "Daylight in decimal hours",
  hoursValue: "{value} h",
  sunrise: "Sunrise",
  solarNoon: "Solar noon",
  sunset: "Sunset",
  change: "Change since the day before",
  changeValue: "{sign}{minutes} min {seconds} s",
  nextDay: "{time} (next day)",
  previousDay: "{time} (previous day)",
  polarDay: "Polar day: the Sun does not set on this date at this latitude.",
  polarNight:
    "Polar night: the Sun does not rise on this date at this latitude.",
  yearHeading: "Daylight through {year}",
  longest: "Longest day of {year}",
  shortest: "Shortest day of {year}",
  dayAndLength: "{date}: {hours} h {minutes} min",
  polarYear:
    "Polar day on {days} days of {year} and polar night on {nights} days.",
  copy: "Copy result",
  copied: "Copied!",
  summary:
    "{place}, {date}: sunrise {sunrise}, sunset {sunset}, {hours} h {minutes} min of daylight.",
  chartLabel: "Daylight hours through the year",
  chartDescription:
    "A line chart of hours of daylight for each day of the year at this latitude, peaking at the summer solstice and bottoming out at the winter solstice, with a marker on the date you chose.",
  chartX: "Month",
  chartY: "Daylight (hours)",
  accuracy:
    "Sunrise and sunset use the NOAA algorithm with the Sun's centre at −0.833° below the horizon, accurate to about a minute between 72° north and south and ten minutes beyond. Hills, buildings and altitude change what you actually see.",
} as const;

export type DayLengthStrings = typeof DAY_LENGTH_CALCULATOR_STRINGS;

interface Form extends PlaceFieldValues {
  date: string;
}

const INITIAL: Form = {
  place: "new-york",
  latitude: "40.7128",
  longitude: "-74.006",
  zone: "America/New_York",
  date: "2026-06-21",
};

export default function DayLengthCalculatorWidget() {
  const strings = useToolUi(DAY_LENGTH_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const [form, setForm] = useState<Form>(INITIAL);

  // Start from today's date once the page is on the reader's machine.
  useEffect(() => {
    setForm((current) => ({
      ...current,
      date: nowInZone(current.zone).date,
    }));
  }, []);

  const patch = (next: Partial<Form>) =>
    setForm((current) => ({ ...current, ...next }));
  const place = readPlace(form);
  const day = parseIsoDay(form.date);

  return (
    <div className="space-y-6">
      <PlaceFields
        labels={strings}
        onChange={patch}
        prefix="dl"
        values={form}
      />
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <TextField
          id="dl-date"
          label={strings.date}
          onChange={(date) => patch({ date })}
          type="date"
          value={form.date}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => patch({ date: nowInZone(form.zone).date })}
          type="button"
        >
          {strings.today}
        </button>
      </div>
      <div aria-live="polite" className="space-y-6">
        {place.ok ? null : <ErrorNotice>{strings.invalidPlace}</ErrorNotice>}
        {place.ok && day === null ? (
          <ErrorNotice>{strings.invalidDate}</ErrorNotice>
        ) : null}
        {place.ok && day !== null ? (
          <DayLengthResults
            date={day}
            form={form}
            latitude={place.latitude}
            locale={locale}
            longitude={place.longitude}
            strings={strings}
          />
        ) : null}
      </div>
    </div>
  );
}
